import {
  AppBase,
  baseFromDoc,
  FirestoreBaseDoc,
  makeDocConverter,
} from '../base/firestore-model-base'
import type {
  TownCompanyAddedBy,
  TownCompanySendStatus,
} from '../../utils/town-company-status'
import {
  parseTownCompanyAddedBy,
  parseTownCompanySendStatus,
} from '../../utils/town-company-status'

/**
 * TownCompany（事業所データ）
 * - Firestore: snake_case + Timestamp。management_id で検索して get/update/delete を行う。
 * - App: camelCase + Date。id はドキュメントID、managementId は management_id フィールド。
 * - 事業所の回答データは TownCompany には持たず、company_survey_answer（survey_answers/current）で保持する。
 */

export type TownCompanyDoc = FirestoreBaseDoc & {
  management_id: string
  company_name: string
  added_by: TownCompanyAddedBy
  postal_code: string
  address: string
  phone: string
  send_status: TownCompanySendStatus
  /** はがき郵送回（データ確定して郵送する時に付与） */
  send_request_number?: string | null
  /** 企業追加リクエストのドキュメントID（イロガミ追加で登録した場合に設定） */
  add_town_company_request_id?: string | null
}

export type TownCompany = AppBase & {
  managementId: string
  companyName: string
  addedBy: TownCompanyAddedBy
  postalCode: string
  address: string
  phone: string
  sendStatus: TownCompanySendStatus
  /** はがき郵送回 */
  sendRequestNumber?: string
  /** 企業追加リクエストのドキュメントID */
  addTownCompanyRequestId?: string
}

export const townCompanyDocConverter = makeDocConverter<TownCompanyDoc>()

export function toTownCompany(id: string, doc: TownCompanyDoc, path: string): TownCompany {
  return {
    ...baseFromDoc(id, doc, path),
    managementId: doc.management_id,
    companyName: doc.company_name,
    addedBy: parseTownCompanyAddedBy(doc.added_by) ?? 'school',
    postalCode: doc.postal_code,
    address: doc.address,
    phone: doc.phone,
    sendStatus: parseTownCompanySendStatus(doc.send_status) ?? 'pending',
    sendRequestNumber: doc.send_request_number ?? (doc as { request_number?: string }).request_number ?? undefined,
    addTownCompanyRequestId:
      doc.add_town_company_request_id ??
      (typeof (doc as unknown as { request_path?: string }).request_path === 'string'
        ? ((doc as unknown as { request_path: string }).request_path.split('/').pop() ?? undefined)
        : undefined),
  }
}

export function toTownCompanyDoc(
  townCompany: Omit<TownCompany, keyof AppBase>,
  base: Pick<FirestoreBaseDoc, 'created_at' | 'updated_at' | 'is_deleted'>,
): TownCompanyDoc {
  return {
    ...base,
    management_id: townCompany.managementId,
    company_name: townCompany.companyName,
    added_by: townCompany.addedBy,
    postal_code: townCompany.postalCode,
    address: townCompany.address,
    phone: townCompany.phone,
    send_status: townCompany.sendStatus,
    send_request_number: townCompany.sendRequestNumber ?? null,
    add_town_company_request_id: townCompany.addTownCompanyRequestId ?? null,
  }
}
